import { useEffect } from 'react';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { getNodesMap, getFunctionsMap, NODE_LEGACY_INT_ID, FUNCTION_NODES } from '@glueapp/graph-collab';

/**
 * Observe the doc's nodes (and nested function nodes) for deletions and prune
 * any stale legacy ids out of the local awareness `selectedLegacyIds`. Without
 * this, a peer deleting a node would leave the local user with selection state
 * pointing at ids that no longer exist.
 *
 * Skips writes for transactions originating from the local client (`origin ===
 * yDoc.clientID`) so the local user's own deletions don't trigger redundant
 * writes from this hook — those paths already manage selection directly.
 */
export function useAwarenessSelectionPruner(yDoc: Y.Doc | null, awareness: Awareness | null): void {
    useEffect(() => {
        if (!yDoc || !awareness) return;

        const collectLiveLegacyIds = (): Set<number> => {
            const liveIds = new Set<number>();
            getNodesMap(yDoc).forEach(node => {
                const legacyId = node.get(NODE_LEGACY_INT_ID);
                if (typeof legacyId === 'number') liveIds.add(legacyId);
            });
            getFunctionsMap(yDoc).forEach(fn => {
                const fnNodes = fn.get(FUNCTION_NODES) as Y.Map<Y.Map<unknown>> | undefined;
                if (!fnNodes) return;
                fnNodes.forEach(node => {
                    const legacyId = node.get(NODE_LEGACY_INT_ID);
                    if (typeof legacyId === 'number') liveIds.add(legacyId);
                });
            });
            return liveIds;
        };

        const handler = (_events: Array<Y.YEvent<any>>, transaction: Y.Transaction) => {
            // Skip transactions we initiated locally; selection is already up-to-date.
            if (transaction.origin === yDoc.clientID) return;

            const localState = awareness.getLocalState();
            const current = localState && Array.isArray(localState.selectedLegacyIds) ? (localState.selectedLegacyIds as number[]) : [];
            if (current.length === 0) return;

            const liveIds = collectLiveLegacyIds();
            const pruned = current.filter(id => liveIds.has(id));
            if (pruned.length === current.length) return;
            awareness.setLocalStateField('selectedLegacyIds', pruned);
        };

        const nodesMap = getNodesMap(yDoc);
        const functionsMap = getFunctionsMap(yDoc);
        nodesMap.observeDeep(handler);
        functionsMap.observeDeep(handler);
        return () => {
            nodesMap.unobserveDeep(handler);
            functionsMap.unobserveDeep(handler);
        };
    }, [yDoc, awareness]);
}
